import { Component, Input, Output, EventEmitter } from '@angular/core'
import { postInContext, postLocked } from 'logic/post-states'
import { withdrawableSoon } from 'src/utils'
import { SubmissionMutations } from 'src/modules/submissions/submission.mutations'
import { successToast } from 'src/toaster'
import { Customer, Post } from 'src/modules/submissions/submission.queries'

type PostAction = 'accept' | 'decline' | 'takeBack' | 'read' | 'review' | 'pin' | 'white-label';

@Component({
  selector: 'post-action-buttons',
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    @media(max-width: 768px) {
      .btn-circle.btn-xs {
        width: 20px;
        height: 20px;
        font-size: 10px;
      }
    }
    .lock-message {
      padding-left: 2px;
      font-weight: bold;
      text-decoration: underline;
    }
  `],
  template: `
    <view-html-modal *ngIf="inContext('purchased') && viewHtmlModalVisible" [post]="post" (onClose)="viewHtmlModalVisible = false"></view-html-modal>
    <redeliver-post-modal *ngIf="redeliverPostModalVisible" [post]="post" [customer]="customer" (onClose)="redeliverPostModalVisible = false"></redeliver-post-modal>
    <div class="d-flex justify-content-between">
      <div class="d-flex align-items-center">
        <ng-template [ngIf]="postQueueLocked">
          <ng-template #lockTooltipTemplate>
            To unlock newer posts, simply accept, decline, or request edits on any submission with a <i class="fas fa-flag text-brick-red"></i>. You can still read or pin this post.
          </ng-template>
          <i class="fas fa-lock"></i>
          <span class="lock-message" [tooltip]="lockTooltipTemplate" placement="bottom" containerClass="white-tooltip" container="body" #tt="bs-tooltip">
            Action needed on an older post...
          </span>
        </ng-template>
        <div *ngIf="isEditspace">
          <span
            [tooltip]="mainButtonsTooltip"
            containerClass="white-tooltip"
            triggers="mouseenter:mouseleave">
            <button
              [class.focus]="activeAction === 'wlApprove'"
              (click)="onAction('wlApprove')"
              id="wl-post-review-{{post.id}}-approve"
              [disabled]="mainButtonsTooltip"
              class="btn btn-sm btn-outline-primary mr-2">
              <i class="fas fa-fw d-inline mr-1"
                [ngClass]="{
                  'fa-chevron-right': activeAction !== 'wlApprove',
                  'fa-chevron-down': activeAction === 'wlApprove'}"></i>
              <span class="d-none d-md-inline">Approve</span>
            </button>
          </span>
          <span
            [tooltip]="mainButtonsTooltip"
            containerClass="white-tooltip"
            triggers="mouseenter:mouseleave">
            <button
              id="wl-post-review-{{post.id}}-request-edits"
              class="btn btn-sm btn-outline-primary mr-2"
              [routerLink]="['/dashboard', customer.id, 'edit_requests', post.id]"
              [disabled]="mainButtonsTooltip">
              {{editspaceRequestEditsButtonString}}
            </button>
          </span>
          <span
            [tooltip]="mainButtonsTooltip"
            containerClass="white-tooltip"
            triggers="mouseenter:mouseleave">
            <button
              [class.focus]="activeAction === 'wlReject'"
              (click)="onAction('wlReject')"
              id="wl-post-review-{{post.id}}-reject"
              [disabled]="mainButtonsTooltip"
              class="btn btn-sm btn-outline-primary mr-2">
              <i class="fas fa-fw d-inline mr-1"
                [ngClass]="{
                  'fa-chevron-right': activeAction !== 'wlReject',
                  'fa-chevron-down': activeAction === 'wlReject'}"></i>
              <span class="d-none d-md-inline">Reject</span>
            </button>
          </span>
        </div>
        <div *ngIf="!isEditspace">
          <button
            *ngIf="post.canPostNow && !postQueueLocked"
            (click)="onAction('accept')"
            id="accept-post-{{post.id}}"
            class="btn btn-sm btn-outline-primary"
            [class.focus]="activeAction === 'accept'">
            <i class="fas fa-fw d-inline mr-1"
              [ngClass]="{
                'fa-chevron-right': activeAction !== 'accept',
                'fa-chevron-down': activeAction === 'accept'}"></i>
            <span class="d-none d-md-inline">Accept</span>
          </button>
          <button
            *ngIf="!postQueueLocked && (post.canRequestEditing || post.lockStatus === 'writer')"
            [routerLink]="['/dashboard', customer.id, 'edit_requests', post.id]"
            id="request-edits-{{post.id}}"
            class="btn btn-sm btn-outline-tertiary mx-2">
            <i class="fas fa-fw fa-pencil-alt d-inline d-md-none"></i>
            <span class="d-none d-md-inline ml-1">
              <span *ngIf="post.lockStatus === 'unlocked'">{{requestEditButtonString}}</span>
              <span *ngIf="['colleague', 'writer', 'white_label'].includes(post.lockStatus)">View edit request</span>
              <span *ngIf="post.lockStatus === 'me'">Finalize edit request</span>
            </span>
          </button>
          <button
            *ngIf="post.canDecline && !postQueueLocked"
            (click)="onAction('decline')"
            class="btn btn-sm btn-outline-tertiary"
            [class.focus]="activeAction === 'decline'">
          <i class="fas fa-fw d-inline mr-1"
            [ngClass]="{
              'fa-chevron-right': activeAction !== 'decline',
              'fa-chevron-down': activeAction === 'decline'}"></i>
            <span class="d-none d-md-inline">Decline</span>
          </button>
          <span *ngIf="post.canUpdateWlVisible" class="d-none d-md-inline mx-2">|</span>
          <span [popover]="disableWhiteLabelTooltipText" triggers="mouseenter:mouseleave">
            <button
              *ngIf="post.canUpdateWlVisible"
              (click)="onAction('white-label')"
              class="btn btn-sm btn-outline-tertiary"
              [class.focus]="activeAction === 'white_label'"
              [disabled] = "!customer.wlVisible">
              <i class="fas fa-chevron d-inline mr-1"
                [ngClass]="{
                  'fa-chevron-right': activeAction !== 'white-label',
                  'fa-chevron-down': activeAction === 'white-label'}"></i>
              <span class="d-none d-md-inline" [innerHTML]="wlBtnContent"></span>
            </button>
          </span>
        </div>
        <button
          *ngIf="!postQueueLocked && post.lockStatus === 'unlocked' && inContext('awaiting_editing')"
          (click)="onAction('takeBack')"
          class="btn btn-sm btn-outline-tertiary ml-1">
          <i class="fab fa-fw fa-get-pocket d-inline d-md-none"></i>
          <span class="d-none d-md-inline">Take back</span>
        </button>
        <ng-template [ngIf]="inContext('purchased') || post.canExportDocX">
          <button
            id="export-{{post.id}}"
            class="btn btn-sm btn-outline-tertiary"
            [class.ml-1]="inContext('queued')"
            [disableRipple]="true"
            [matMenuTriggerFor]="purchaseExportMenu"
          >
            Export<i class="fa fa-caret-down ml-1"></i>
          </button>
          <mat-menu #purchaseExportMenu="matMenu" xPosition="after">
            <button mat-menu-item (click)="onAction('copy')">
              <i class="far fa-copy fa-fw"></i>
              <span class="ml-1">Copy</span>
            </button>
            <button
              *ngIf="inContext('purchased')"
              mat-menu-item
              (click)="viewHtmlModalVisible = true"
            >
              <i class="far fa-file-code fa-fw"></i>
              <span class="ml-1">HTML</span>
            </button>
            <a
              *ngIf="post.canExportDocX"
              [href]="downloadDocXPath"
              mat-menu-item
              style="text-decoration: none; color: inherit;"
            >
              <i class="far fa-file-word fa-fw"></i>
              <span class="ml-1">DocX</span>
            </a>
            <button
              *ngIf="inContext('purchased')"
              mat-menu-item
              (click)="redeliverPostModalVisible = true"
              class="no-decoration">
              <i class="far fa-share-square fa-fw"></i>
              <span class="ml-1">Redeliver</span>
            </button>
          </mat-menu>
        </ng-template>
      </div>

      <!-- right side (second flex child) -->
      <div>
        <ng-template [ngIf]="['colleague', 'writer', 'white_label'].includes(post.lockStatus)">
          <i class="fas fa-lock fa-xs ml-2"></i>
          <i><span class="small" *ngIf="post.lockStatus !== 'writer'"><strong class="ml-1">{{post.user?.fullName}}</strong> is logging feedback</span></i>
          <i><span class="small" *ngIf="post.lockStatus === 'writer'" [innerHTML]="writingMakingEditsMessage"></span></i>
        </ng-template>
        <ng-template [ngIf]="post.lockStatus === 'me'">
          <i *ngIf="!post.canUpdateWlPostReview"><span class="small"><strong class="ml-2">{{post.writer?.shortDisplayName}}</strong> is waiting for your feedback</span></i>
          <i *ngIf="post.canUpdateWlPostReview"><span class="small">Agency still waiting for feedback</span></i>
        </ng-template>

        <button *ngIf="inContext('purchased')" class="btn btn-sm btn-outline-tertiary mr-1" [ngClass]="{'btn-default btn-outline-default': !post.customerPublished}" (click)="togglePublished(!post?.customerPublished)">
          <i class="fas fa-check d-inline d-md-none"></i>
          <span class="d-none d-md-inline">{{post?.customerPublished ? "Marked as Published" : "Mark Published"}}</span>
        </button>
        <button (click)="onAction('review')" *ngIf="!inContext('purchaseable')" class="btn btn-sm btn-outline-tertiary mr-1" [class.focus]="activeAction === 'review'">
          <i class="fa-fw d-inline d-md-none" [ngClass]="{'far fa-comment': post?.appPostReview, 'fas fa-comment-dots': !post?.appPostReview}"></i>
          <span class="d-none d-md-inline">{{post?.appPostReview ? "Reviewed" : "Review"}}</span>
        </button>
        <rating-stars *ngIf="!inContext('purchaseable')" [value]="post.appRating?.value" (onChange)="onRating($event)"></rating-stars>
      </div>
    </div>
  `
})
export class PostActionButtons {
  @Input() customer: Customer
  @Input() post: Post
  @Input() activeAction: string = '';
  @Input() expanded: boolean = false
  @Input() isEditspace: boolean = false
  @Input() contentTarget: HTMLElement
  @Output() action: EventEmitter<PostAction> = new EventEmitter<PostAction>();
  @Output() rating: EventEmitter<number> = new EventEmitter<number>();
  @Output() deprecatedStoreChange: EventEmitter<number> = new EventEmitter<number>()
  viewHtmlModalVisible: boolean = false;
  redeliverPostModalVisible: boolean = false;

  constructor(private submissionMutations: SubmissionMutations) { }

  get postQueueLocked(): boolean {
    return postLocked(this.post.submissionPosition, this.customer) && !this.isEditspace
  }

  get writingMakingEditsMessage(): string {
    let message: string
    if (this.post.writer?.state === 'unavailable') {
      message = `Your edit request has been sent`
    } else {
      message = `<strong class="ml-1">${this.post.writer?.shortDisplayName}</strong> is making edits`
    }
    return message
  }

  get downloadDocXPath(): string {
    return `/api/customers/${this.customer.id}/posts/${this.post.id}/docx.json`
  }

  get disableWhiteLabelTooltipText(): string {

    let message = ''


    if (!this.customer.wlVisible) {
      message = "Adjust your White Label preferences to make content in this Submissions Queue visible to your client. "
    }

    return message
  }

  get mainButtonsTooltip(): string {
    if (this.inContext('awaiting_editing') || this.inContext('awaiting_agency_review')) {
      return "Our team is reviewing your feedback. Contact us if you've changed your mind!"
    } else {
      return null
    }
  }

  get clientRequestedEdits(): boolean {
    return this.post.wlUserFeedbackMessage.includes('Your client requested edits')
  }

  get requestEditButtonString(): string {
    return this.clientRequestedEdits ? 'Finish edit request' : 'Request edits'
  }

  get editspaceRequestEditsButtonString(): string {
    return this.post.lockStatus === 'me' ? 'Finalize edit request' : 'Request edits'
  }

  get wlBtnContent(): string {
    let btnContent = 'White label'
    const action = this.post.wlPostReview?.action
    let icon: string
    if (action === 'approve') {
      icon = 'thumbs-up'
    } else if (action === 'reject') {
      icon = 'thumbs-down'
    } else if (this.clientRequestedEdits) {
      icon = 'highlighter'
    }
    if (icon) btnContent += `<i class='fas fa-${icon} ml-1'></i>`
    return btnContent
  }

  inContext(context: string): boolean {
    return postInContext(this.post, context, this.isEditspace)
  }

  togglePublished(published: boolean): void {
    this.submissionMutations.updatePost({ post: this.post, updates: { customerPublished: published } }).then(post => {
      const markedAs = published ? 'published' : 'not published'
      successToast(`Marked ${markedAs}`)
    })
  }

  onAction(action: PostAction): void {
    (<HTMLElement>document.activeElement).blur()
    this.action.emit(action)
  }

  onRating(rating: number): void {
    this.rating.emit(rating)
  }

  withdrawableSoon(post: Post): boolean {
    return withdrawableSoon(post)
  }

}
